import { Stack } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material/Select'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { SelectFieldOption } from '@/components/inputs/CustomSelectField'
import CustomSelectField from '@/components/inputs/CustomSelectField'
import { FIELD_WIDTH } from '@/constants/layout'
import type { ResourceGlobalFilters, ResourceType } from '@/features/resource/types'
import { getResourceTypeOptions } from '@/features/resource/utils/resourceTypes'
import type { Site } from '@/features/site/types/site'
import { getSiteOptions } from '@/features/site/utils/getSiteOptions'
import { sortOptionsAlphabetically } from '@/utils/sortOptionsAlphabetically'

export type ResourcesAppBarContentProps = {
  sites: Site[]
  resourceTypes: ResourceType[]
  countryCodeOptions: SelectFieldOption<string>[]
  globalFilters: ResourceGlobalFilters
  onSiteChange: (event: SelectChangeEvent) => void
  onResourceTypeChange: (event: SelectChangeEvent) => void
  onCountryCodeChange: (event: SelectChangeEvent) => void
}

const ResourcesAppBarContent = ({
  sites,
  resourceTypes,
  globalFilters,
  countryCodeOptions,
  onSiteChange,
  onResourceTypeChange,
  onCountryCodeChange,
}: ResourcesAppBarContentProps) => {
  const { t } = useTranslation()
  const sortedResourceTypeOptions = useMemo(
    () => sortOptionsAlphabetically<ResourceType>(getResourceTypeOptions(resourceTypes, t)),
    [resourceTypes],
  )
  const sortedSitesOptions = useMemo(() => sortOptionsAlphabetically<string>(getSiteOptions(sites)), [sites])

  return (
    <Stack sx={{ flexDirection: 'row', gap: 1 }}>
      <CustomSelectField
        showAllOption
        id="country-code"
        label={t('resources.country_code_filter_label')}
        options={countryCodeOptions}
        size="medium"
        sx={{ minWidth: FIELD_WIDTH }}
        value={globalFilters.countryCode ?? ''}
        variant="outlinedWhite"
        onChange={onCountryCodeChange}
      />

      <CustomSelectField
        showAllOption
        id="sites"
        label={t('resources.customers_filter_label')}
        options={sortedSitesOptions}
        size="medium"
        sx={{ minWidth: FIELD_WIDTH }}
        value={globalFilters.siteUuid ?? ''}
        variant="outlinedWhite"
        onChange={onSiteChange}
      />

      <CustomSelectField
        showAllOption
        id="resource-type"
        label={t('resources.resource_types_filter_label')}
        options={sortedResourceTypeOptions}
        size="medium"
        sx={{ minWidth: FIELD_WIDTH }}
        value={globalFilters.resourceType ?? ''}
        variant="outlinedWhite"
        onChange={onResourceTypeChange}
      />
    </Stack>
  )
}

export default ResourcesAppBarContent
